import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import service from '../service';
import type { Asset, UpdateAssetPayload } from '@Types/assets';
import { assetsKeys } from './useAssets';
import { assetTagKeys } from './useAssetTags';

export interface UpdateAssetVariables {
  id: string;
  payload: UpdateAssetPayload;
}

export type UpdateAssetResult = Asset;

export function useUpdateAsset(
  options?: UseMutationOptions<UpdateAssetResult, Error, UpdateAssetVariables>,
): UseMutationResult<UpdateAssetResult, Error, UpdateAssetVariables> {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<UpdateAssetResult, Error, UpdateAssetVariables>({
    mutationKey: ['asset', 'update'],
    mutationFn: async ({ id, payload }) => {
      return service.updateAsset(id, payload);
    },
    retry: (failureCount, error: any) => {
      const status =
        error?.status ??
        error?.response?.status ??
        error?.cause?.status ??
        null;
      const isServerOrNetwork = status == null || status >= 500;
      return failureCount < 2 && isServerOrNetwork;
    },
    ...restOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: assetsKeys.all });
      queryClient.invalidateQueries({ queryKey: assetTagKeys.all });
      onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
